/**
 * BusinessCalendario — calendario de CAJA (glass Ola A).
 *
 * Junta obligaciones DIAN/nómina, pagos Mateo, cobros y avisos de liquidación
 * (3 días hábiles antes). Semáforo conservador: no cuenta lo que te deben.
 * Soft CTAs a Provisiones / Rendimientos / Pagos — no mueve plata aquí.
 * Visual: tokens --fx-* / .cal-ola (claro + oscuro), sin pasteles Tailwind.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCalendarioBusiness, getSuscripcionCalendario } from '../../lib/cerebro';

const cop = (n) => `$${Math.round(Number(n) || 0).toLocaleString('es-CO')}`;

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

function partes(iso) {
  const [y, m, d] = String(iso).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return { y, m, d, dow: dt.getUTCDay(), mesNombre: MESES[m - 1], diaNombre: DIAS[dt.getUTCDay()] };
}

function diasDesdeHoy(iso) {
  const { y, m, d } = partes(iso);
  const hoy = new Date();
  const h = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  return Math.round((Date.UTC(y, m - 1, d) - h) / 86400000);
}

function digitoNit(nit) {
  const s = String(nit || '').replace(/\D/g, '');
  return s ? s.slice(-1) : null;
}

const TIPO_META = {
  obligacion: { label: 'Impuesto / obligación', tone: 'blue' },
  pago: { label: 'Pagar', tone: 'amber' },
  cobro: { label: 'Te deben', tone: 'green' },
  aviso: { label: 'Preparar caja', tone: 'violet' },
  festivo: { label: 'Festivo', tone: 'muted' },
};

const SEMAFORO_META = {
  verde: { label: 'Tranquilo', tone: 'verde' },
  amarillo: { label: 'Ajustado', tone: 'amarillo' },
  rojo: { label: 'Ojo', tone: 'rojo' },
};

const FILTROS = [
  { id: 'proximos', label: 'Próximos 30 días' },
  { id: 'todos', label: 'Todo' },
  { id: 'obligacion', label: 'Impuestos' },
  { id: 'pago', label: 'Por pagar' },
  { id: 'cobro', label: 'Por cobrar' },
  { id: 'aviso', label: 'Avisos' },
  { id: 'festivo', label: 'Festivos' },
];

function etiquetaCuando(faltan, vencida) {
  if (vencida) return 'vencida';
  if (faltan < 0) return 'pasada';
  if (faltan === 0) return 'hoy';
  if (faltan === 1) return 'mañana';
  return `en ${faltan} días`;
}

function EventoCard({ e }) {
  const meta = TIPO_META[e.tipo] || TIPO_META.festivo;
  const { d, diaNombre, mesNombre } = partes(e.fecha);
  const faltan = diasDesdeHoy(e.fecha);
  const vencida = !!e.vencida || (faltan < 0 && e.direccion === 'sale');

  return (
    <article className={`cal-ola__evento${vencida ? ' is-vencida' : ''}`}>
      <div className={`cal-ola__fecha cal-ola__fecha--${meta.tone}`}>
        <span className="cal-ola__fecha-d">{d}</span>
        <span className="cal-ola__fecha-m">{diaNombre.slice(0, 3)} · {mesNombre.slice(0, 3)}</span>
      </div>
      <div className="cal-ola__evento-body">
        <div className="cal-ola__evento-label">{meta.label}</div>
        <div className="cal-ola__evento-title">{e.titulo}</div>
        <div className="cal-ola__evento-meta">
          <span className={vencida ? 'is-bad' : ''}>
            {etiquetaCuando(faltan, vencida)}
            {e.diasMora ? ` · ${e.diasMora}d mora` : ''}
          </span>
          {e.fuenteTexto && (
            <span className={e.exacta ? 'is-good' : 'is-warn'}>{e.fuenteTexto}</span>
          )}
          {e.detalle && <span className="cal-ola__evento-detalle">{e.detalle}</span>}
        </div>
      </div>
      {e.monto > 0 && (
        <div className={`cal-ola__monto${e.direccion === 'entra' ? ' is-in' : ''}`}>
          {e.direccion === 'entra' ? '+' : '−'}{cop(e.monto)}
        </div>
      )}
    </article>
  );
}

export default function BusinessCalendario() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [sus, setSus] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [filtro, setFiltro] = useState('proximos');

  useEffect(() => {
    getCalendarioBusiness()
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.message || 'No pude cargar el calendario.'));
  }, []);

  async function pedirLink() {
    try { setSus(await getSuscripcionCalendario()); }
    catch { setErr('No pude generar el link.'); }
  }
  function copiar(txt) {
    navigator.clipboard?.writeText(txt);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  const eventosFiltrados = useMemo(() => {
    if (!data?.eventos) return [];
    const list = data.eventos;
    if (filtro === 'todos') return list;
    if (filtro === 'proximos') {
      return list.filter((e) => {
        if (e.tipo === 'festivo') return false;
        const d = diasDesdeHoy(e.fecha);
        return e.vencida || (d >= 0 && d <= 30);
      });
    }
    return list.filter((e) => e.tipo === filtro);
  }, [data, filtro]);

  const meses = useMemo(() => {
    const out = [];
    for (const e of eventosFiltrados) {
      const { y, m, mesNombre } = partes(e.fecha);
      const key = `${y}-${m}`;
      let g = out.find((x) => x.key === key);
      if (!g) { g = { key, titulo: `${mesNombre} ${y}`, eventos: [] }; out.push(g); }
      g.eventos.push(e);
    }
    return out;
  }, [eventosFiltrados]);

  const proximosCriticos = useMemo(() => {
    if (!data?.eventos) return [];
    return data.eventos
      .filter((e) => e.tipo !== 'festivo' && (e.direccion === 'sale' || e.tipo === 'aviso' || e.vencida))
      .filter((e) => e.vencida || (diasDesdeHoy(e.fecha) >= 0 && diasDesdeHoy(e.fecha) <= 30))
      .slice(0, 5);
  }, [data]);

  if (err) {
    return (
      <div className="cal-ola">
        <div className="cal-ola__err">{err}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="cal-ola">
        <div className="cal-ola__loading">Armando tu calendario de caja…</div>
      </div>
    );
  }
  if (data.sinEmpresa) {
    return (
      <div className="cal-ola">
        <div className="cal-ola__loading">{data.mensaje || 'Vincula tu empresa para ver el calendario.'}</div>
      </div>
    );
  }

  const p = data.proyeccion || {};
  const sem = SEMAFORO_META[p.semaforo] || SEMAFORO_META.verde;
  const digito = digitoNit(data.nit);
  const haySalidasProximas = proximosCriticos.some((e) => e.direccion === 'sale' && e.monto > 0);

  return (
    <div className="cal-ola">
      <header className="cal-ola__head">
        <p className="cal-ola__eyebrow">Tesorería</p>
        <h1 className="cal-ola__title">Calendario de caja</h1>
        <p className="cal-ola__sub">
          Impuestos, nómina, lo que debes y lo que te deben — con semáforo de si alcanza.
          {data.empresa && (
            <span className="cal-ola__entity">
              {data.empresa}
              {data.nit ? ` · NIT ${data.nit}` : ''}
              {digito != null ? ` · terminación ${digito} (fechas DIAN)` : ''}
            </span>
          )}
        </p>
      </header>

      <section className={`cal-ola__semaforo cal-ola__semaforo--${sem.tone}`}>
        <div className="cal-ola__semaforo-top">
          <span className="cal-ola__semaforo-label">{sem.label}</span>
          {p.saldoHoy != null && p.fuenteSaldo !== 'desconocido' && (
            <span className="cal-ola__semaforo-caja">Caja hoy {cop(p.saldoHoy)}</span>
          )}
        </div>
        <p className="cal-ola__semaforo-text">{p.texto}</p>
        {p.totalEntra > 0 && (
          <p className="cal-ola__semaforo-note">
            El semáforo no cuenta lo que te deben ({cop(p.totalEntra)}): todavía no es caja.
            {p.saldoSiTePaganTodo != null && (
              <> Si te pagan todo, quedarías en torno a {cop(p.saldoSiTePaganTodo)}.</>
            )}
          </p>
        )}
        {haySalidasProximas && (
          <div className="cal-ola__cta-row">
            <Link to="/business/provisiones" className="cal-ola__btn cal-ola__btn--ghost">
              Apartar en provisiones
            </Link>
            <Link to="/business/rendimientos" className="cal-ola__btn cal-ola__btn--ghost">
              Poner a trabajar lo que espera
            </Link>
            <Link to="/business/pagos" className="cal-ola__btn cal-ola__btn--primary">
              Ir a pagos
            </Link>
          </div>
        )}
      </section>

      {proximosCriticos.length > 0 && filtro === 'proximos' && (
        <section className="cal-ola__block">
          <h2 className="cal-ola__h2">Lo que te pega a la caja (30 días)</h2>
          <p className="cal-ola__hint">
            Avisos 3 días hábiles antes: para que el sobre salga a tiempo, sin sorpresa.
          </p>
          <div className="cal-ola__list">
            {proximosCriticos.map((e, i) => (
              <EventoCard key={`crit-${e.fecha}-${i}`} e={e} />
            ))}
          </div>
        </section>
      )}

      <section className="cal-ola__panel">
        <div className="cal-ola__panel-row">
          <div>
            <h2 className="cal-ola__h2 tight">En tu celular</h2>
            <p className="cal-ola__hint">
              Suscríbelo en Google Calendar o iPhone. Aviso 3 días antes, entre tus reuniones.
            </p>
          </div>
          {!sus && (
            <button type="button" onClick={pedirLink} className="cal-ola__btn cal-ola__btn--primary">
              Generar mi link
            </button>
          )}
        </div>
        {sus && (
          <div className="cal-ola__sus">
            <div className="cal-ola__cta-row">
              <a href={sus.urlSuscripcion} className="cal-ola__btn cal-ola__btn--primary">
                Suscribir en mi calendario
              </a>
              <button type="button" onClick={() => copiar(sus.url)} className="cal-ola__btn cal-ola__btn--ghost">
                {copiado ? '¡Copiado!' : 'Copiar link'}
              </button>
            </div>
            <p className="cal-ola__warn-note">
              Link privado: quien lo tenga ve tus fechas y montos. No lo compartas.
            </p>
          </div>
        )}
      </section>

      <div className="cal-ola__chips">
        {FILTROS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFiltro(f.id)}
            className={`cal-ola__chip${filtro === f.id ? ' on' : ''}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtro === 'cobro' && (
        <p className="cal-ola__hint">
          Los cobros se ven aquí pero no entran al semáforo.{' '}
          <Link to="/business/cartera" className="cal-ola__link">Abrir cartera</Link>
        </p>
      )}

      {meses.length === 0 ? (
        <p className="cal-ola__empty">
          No hay eventos en este filtro. Prueba “Todo” o agrega montos en Caja / Provisiones.
        </p>
      ) : (
        meses.map((g) => (
          <section key={g.key} className="cal-ola__block">
            <h3 className="cal-ola__month">{g.titulo}</h3>
            <div className="cal-ola__list">
              {g.eventos.map((e, i) => (
                <EventoCard key={`${g.key}-${e.fecha}-${i}`} e={e} />
              ))}
            </div>
          </section>
        ))
      )}

      <p className="cal-ola__foot">
        Las fechas DIAN salen del calendario oficial según la terminación de tu NIT.
        Las aproximadas confírmalas con tu contador. FlowexPay no declara impuestos por ti:
        te ayuda a que la caja esté lista a tiempo.
      </p>
    </div>
  );
}
